import {
  Icon,
  IconButton,
  SelectionList,
  Tooltip,
} from '@dixa/lib-shared-habla';
import type { Column } from '@tanstack/react-table';
import classNames from 'classnames';
import type { SearchConversationEnriched } from 'lib-search-types';
import { type MouseEvent, useRef } from 'react';

import {
  type DragMoveEvent,
  useOrderedDragDrop,
} from 'utils/useOrderedDragDrop';

import type { ConversationsTableFeatures } from '../../../../../../conversationsTable.columns';

import styles from './conversationsTablePreferencesColumnsItem.module.scss';

const dragType = Symbol('conversationsTableColumn');

export type ConversationsTablePreferencesColumnsItemProps = {
  /** The column the item shows and hides. */
  column: Column<ConversationsTableFeatures, SearchConversationEnriched>;
  /** The name of the column shown to the user. */
  label: string;
  /** The position of the item in the list. */
  index: number;
  /** Called when the item is dropped onto another item. */
  onMove: (event: DragMoveEvent) => void;
  /** Called when the user pins or unpins the column. */
  onIsPinnedChange: (isPinned: boolean) => void;
};

/** A column in the table preferences that can be toggled, pinned and dragged to reorder. */
export const ConversationsTablePreferencesColumnsItem = ({
  column,
  label,
  index,
  onMove,
  onIsPinnedChange,
}: ConversationsTablePreferencesColumnsItemProps): JSX.Element => {
  const itemRef = useRef<HTMLLIElement>(null);
  const handleRef = useRef<HTMLSpanElement>(null);
  const canHide = column.getCanHide();
  const isPinned = column.getIsPinned() === 'start';

  const { isDragging, isOver } = useOrderedDragDrop({
    type: dragType,
    index,
    source: handleRef,
    target: itemRef,
    preview: itemRef,
    onMove,
    triggerSwap: 'drop',
  });

  return (
    <SelectionList.Item
      ref={itemRef}
      value={column.id}
      isDisabled={!canHide}
      // Keeps the pin button out of the option's name.
      aria-label={label}
      className={classNames(styles.root, {
        [styles.rootDragging]: isDragging,
        [styles.rootOver]: isOver && !isDragging,
      })}
      contentClassName={styles.content}
    >
      {canHide && (
        <span
          ref={handleRef}
          className={styles.dragHandle}
          data-testid="column-drag-handle"
          onClick={(e: MouseEvent) => e.stopPropagation()}
        >
          <Icon name="drag" />
        </span>
      )}

      <span className={classNames({ [styles.labelDisabled]: !canHide })}>
        {label}
      </span>

      <Tooltip content={isPinned ? 'Unpin column' : 'Pin column'}>
        <IconButton
          icon={isPinned ? 'thumbtack-slash' : 'thumbtack'}
          variant="tertiary"
          aria-label={isPinned ? `Unpin ${label}` : `Pin ${label}`}
          className={styles.pinButton}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onIsPinnedChange(!isPinned);
          }}
        />
      </Tooltip>
    </SelectionList.Item>
  );
};
